<script setup lang="ts">
import { onMounted } from 'vue'

import AddUserDialog, { type AddUserPayload } from '../components/AddUserDialog.vue'
import AssignProfilesDialog, { type AssignProfilesPayload } from '../components/AssignProfilesDialog.vue'
import CapabilityProfilesPanel from '../components/CapabilityProfilesPanel.vue'
import TopBar from '../components/TopBar.vue'
import UserTable from '../components/UserTable.vue'
import { useAdmin } from '../composables/useAdmin'
import { useAuth } from '../composables/useAuth'
import { useCapabilityProfiles } from '../composables/useCapabilityProfiles'
import type { UserRow } from '../types/callables'
import Shell from '../ui/components/Shell.vue'
import { openDialog } from '../ui/dialogs'
import { toast } from '../ui/toasts'

const { user } = useAuth()
const { users, loading, error, nextToken, load, setClaim, createUser } = useAdmin()

// Profiles are loaded independently — the panel manages its own state, but we
// also need the list here to pass into UserTable for name resolution and into
// AssignProfilesDialog. Loading here (rather than reading through the panel's
// composable state) keeps the parent-child dataflow one-way.
const { profiles: allProfiles, load: loadProfiles } = useCapabilityProfiles()

onMounted(() => {
  void load(true)
  void loadProfiles()
})

async function onToggleActive(uid: string, value: boolean) {
  const ok = await setClaim(uid, { active: value })
  if (ok) toast.success(value ? 'Account activated.' : 'Account deactivated.')
}

async function onToggleAdmin(uid: string, value: boolean) {
  const ok = await setClaim(uid, { role: value ? 'admin' : null })
  if (ok) toast.success(value ? 'Admin access granted.' : 'Admin access removed.')
}

async function onAddUser() {
  const created = await openDialog<AddUserPayload>(AddUserDialog, {
    create: (payload: AddUserPayload) => createUser(payload),
  })
  if (created) toast.success(`Created ${created.email}.`)
}

async function onEditAssignments(u: UserRow) {
  const saved = await openDialog<AssignProfilesPayload>(AssignProfilesDialog, {
    user: u,
    profiles: allProfiles.value,
    save: async (payload: AssignProfilesPayload) => {
      const ok = await setClaim(payload.uid, {
        profiles:       payload.profiles,
        defaultProfile: payload.defaultProfile,
      })
      if (ok) {
        // Assignment counts on the profile panel are derived from users/{uid}
        // reads; a setClaim that changed the profiles list must refetch so the
        // panel's "Assigned" column stays truthful.
        void loadProfiles()
      }
      return ok
    },
  })
  if (saved) toast.success('Profile assignments updated.')
}
</script>

<template>
  <Shell>
    <template #header>
      <TopBar label="Admin" />
    </template>

    <main class="content">
      <CapabilityProfilesPanel @users-changed="load(true)" />

      <header class="bar">
        <h1>Users</h1>
        <div class="spacer" />
        <button type="button" class="btn btn-sm" :disabled="loading" @click="load(true)">
          Refresh
        </button>
        <button type="button" class="btn btn-primary btn-sm" @click="onAddUser">Add user</button>
      </header>

      <p class="propagation">
        Claim changes reach the target user on their next token refresh — within
        an hour, or immediately if they sign out and back in. Profile
        assignments take effect on their next licence fetch (a page reload).
      </p>

      <p v-if="error" class="msg msg--error" role="alert">{{ error }}</p>

      <UserTable
        :users="users"
        :self-uid="user?.uid ?? null"
        :profiles="allProfiles"
        :loading="loading"
        @toggle-active="onToggleActive"
        @toggle-admin="onToggleAdmin"
        @edit-assignments="onEditAssignments"
      />

      <div class="footer">
        <span v-if="loading" class="footer-note muted">Loading…</span>
        <button v-if="nextToken" type="button" class="btn btn-sm" :disabled="loading" @click="load(false)">
          Load more
        </button>
      </div>
    </main>
  </Shell>
</template>

<style scoped>
/* View-unique layout: max-width content column centred inside the Shell's
   scrollable body. The specific 72rem cap and 1.25rem padding are
   admin-only — no shared .container primitive earns its keep yet. */
.content {
  padding: 1.25rem;
  max-width: 72rem;
  width: 100%;
  margin: 0 auto;
}

.bar {
  display: flex;
  align-items: center;
  gap: var(--sp-2);
  margin-bottom: var(--sp-2);
}

h1 { margin: 0; font-size: 1.15rem; font-weight: 600; }

.propagation {
  margin: 0 0 var(--sp-4);
  font-size: 0.8rem;
  color: var(--fg-muted);
}

.footer {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: var(--sp-3);
  padding: var(--sp-4) 0;
}

.footer-note { font-size: 0.85rem; }
</style>
